import React, { useEffect } from 'react';
import { Calendar, Clock, Sun, Moon } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { CustomPattern, CustomDayPattern } from '@/types/schedule.types';

interface CustomScheduleFormProps {
  pattern: CustomPattern;
  onChange: (pattern: CustomPattern) => void;
  disabled?: boolean;
}

type ShiftType = 'day' | 'night' | 'off';

// Popular custom shift presets
const CUSTOM_PRESETS = [
  { 
    label: 'День-Ночь-Вых-Вых', 
    cycle_length: 4,
    pattern: [
      { day: 1, isWorking: true, start: '08:00', end: '20:00' },
      { day: 2, isWorking: true, start: '20:00', end: '08:00' },
      { day: 3, isWorking: false },
      { day: 4, isWorking: false },
    ]
  },
  { 
    label: 'День-День-Ночь-Ночь-Вых-Вых', 
    cycle_length: 6,
    pattern: [
      { day: 1, isWorking: true, start: '08:00', end: '20:00' },
      { day: 2, isWorking: true, start: '08:00', end: '20:00' },
      { day: 3, isWorking: true, start: '20:00', end: '08:00' },
      { day: 4, isWorking: true, start: '20:00', end: '08:00' },
      { day: 5, isWorking: false },
      { day: 6, isWorking: false },
    ]
  },
  { 
    label: 'Сутки через трое', 
    cycle_length: 4,
    pattern: [
      { day: 1, isWorking: true, start: '08:00', end: '08:00' },
      { day: 2, isWorking: false },
      { day: 3, isWorking: false },
      { day: 4, isWorking: false },
    ]
  },
];

const getShiftType = (day: CustomDayPattern): ShiftType => {
  if (!day.isWorking) return 'off';
  const startHour = day.start ? parseInt(day.start.split(':')[0]) : 9;
  // Night shift typically starts at 18:00 or later
  return startHour >= 18 ? 'night' : 'day';
};

export const CustomScheduleForm: React.FC<CustomScheduleFormProps> = ({
  pattern,
  onChange,
  disabled = false,
}) => {
  // Initialize pattern if empty
  useEffect(() => {
    if (pattern.pattern.length === 0 && pattern.cycle_length > 0) {
      const newPatternDays: CustomDayPattern[] = [];
      for (let i = 1; i <= pattern.cycle_length; i++) {
        newPatternDays.push({ day: i, isWorking: true, start: '09:00', end: '18:00' });
      }
      onChange({ ...pattern, pattern: newPatternDays });
    }
  }, [pattern.pattern.length, pattern.cycle_length, onChange, pattern]);

  const updateCycleLength = (length: number) => {
    if (length < 1 || length > 31) return;
    
    const newPatternDays: CustomDayPattern[] = [];
    for (let i = 1; i <= length; i++) {
      const existing = pattern.pattern.find(p => p.day === i);
      newPatternDays.push(existing || { day: i, isWorking: true, start: '09:00', end: '18:00' });
    }
    onChange({ ...pattern, cycle_length: length, pattern: newPatternDays });
  };

  const updateDay = (dayNum: number, updates: Partial<CustomDayPattern>) => {
    const newPatterns = pattern.pattern.map(p =>
      p.day === dayNum ? { ...p, ...updates } : p
    );
    onChange({ ...pattern, pattern: newPatterns });
  };

  const setShiftType = (dayNum: number, type: ShiftType) => {
    if (type === 'off') {
      updateDay(dayNum, { isWorking: false, start: undefined, end: undefined });
    } else if (type === 'day') {
      updateDay(dayNum, { isWorking: true, start: '08:00', end: '20:00' });
    } else if (type === 'night') {
      updateDay(dayNum, { isWorking: true, start: '20:00', end: '08:00' });
    }
  };

  const applyPreset = (preset: typeof CUSTOM_PRESETS[0]) => {
    onChange({
      type: 'custom',
      cycle_length: preset.cycle_length,
      cycle_start_date: pattern.cycle_start_date, // Keep existing date
      pattern: preset.pattern.map(p => ({ ...p })),
    });
  };

  // Generate preview (repeat pattern to show more days)
  const generatePreview = () => {
    if (pattern.pattern.length === 0) return [];
    
    const preview = [];
    for (let i = 0; i < 16; i++) {
      const positionInCycle = i % pattern.cycle_length;
      const dayPattern = pattern.pattern.find(p => p.day === positionInCycle + 1);
      if (dayPattern) {
        preview.push({
          displayDay: i + 1,
          ...dayPattern,
          shiftType: getShiftType(dayPattern),
        });
      }
    }
    return preview;
  };

  const preview = generatePreview();

  return (
    <div className="space-y-6">
      {/* Quick presets */}
      <div className="space-y-2">
        <Label>Быстрый выбор</Label>
        <div className="flex flex-wrap gap-2">
          {CUSTOM_PRESETS.map((preset) => (
            <Button
              key={preset.label}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => applyPreset(preset)}
              disabled={disabled}
            >
              {preset.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Cycle length */}
      <div className="rounded-lg border p-4 space-y-4 bg-card">
        <h4 className="text-sm font-semibold flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Настройка цикла
        </h4>

        <div className="space-y-2">
          <Label htmlFor="cycle_length">
            Длина цикла (дней) <span className="text-destructive">*</span>
          </Label>
          <div className="flex items-center gap-2">
            <Input
              id="cycle_length"
              type="number"
              min="1"
              max="31"
              value={pattern.cycle_length}
              onChange={(e) => updateCycleLength(parseInt(e.target.value) || 1)}
              disabled={disabled}
              className="w-24"
            />
            <span className="text-sm text-muted-foreground">дней в цикле</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Укажите количество дней в цикле (например, 4 для графика "День-Ночь-Вых-Вых")
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="cycle_start_date">
            Дата начала цикла <span className="text-destructive">*</span>
          </Label>
          <Input
            id="cycle_start_date"
            type="date"
            value={pattern.cycle_start_date || ''}
            onChange={(e) => onChange({ ...pattern, cycle_start_date: e.target.value })}
            disabled={disabled}
            className="w-48"
          />
          <p className="text-xs text-muted-foreground">
            Укажите дату, с которой начинается цикл (например, первый рабочий день)
          </p>
        </div>
      </div>

      {/* Days pattern */}
      {pattern.pattern.length > 0 && (
        <div className="rounded-lg border p-4 space-y-4 bg-card">
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Расписание по дням цикла
          </h4>

          <div className="space-y-2">
            {pattern.pattern.map((day) => {
              const shiftType = getShiftType(day);
              
              return (
                <div
                  key={day.day}
                  className="flex items-center gap-3 rounded-md border p-3 bg-background"
                >
                  <span className="w-16 text-sm font-medium">День {day.day}</span>

                  <Select
                    value={shiftType}
                    onValueChange={(v) => setShiftType(day.day, v as ShiftType)}
                    disabled={disabled}
                  >
                    <SelectTrigger className="w-44">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="day">
                        <span className="flex items-center gap-2">
                          <Sun className="h-4 w-4 text-amber-500" />
                          Дневная смена
                        </span>
                      </SelectItem>
                      <SelectItem value="night">
                        <span className="flex items-center gap-2">
                          <Moon className="h-4 w-4 text-indigo-500" />
                          Ночная смена
                        </span>
                      </SelectItem>
                      <SelectItem value="off">
                        <span className="flex items-center gap-2">
                          <span className="h-4 w-4 flex items-center justify-center text-gray-400">—</span>
                          Выходной
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>

                  {day.isWorking ? (
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        type="time"
                        value={day.start || ''}
                        onChange={(e) => updateDay(day.day, { start: e.target.value })}
                        disabled={disabled}
                        className="w-32"
                      />
                      <span className="text-muted-foreground">—</span>
                      <Input
                        type="time"
                        value={day.end || ''}
                        onChange={(e) => updateDay(day.day, { end: e.target.value })}
                        disabled={disabled}
                        className="w-32"
                      />
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground flex-1">
                      Нерабочий день
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Preview */}
      {preview.length > 0 && (
        <div className="rounded-lg border p-4 bg-muted/30 space-y-3">
          <h4 className="text-sm font-semibold">Превью паттерна</h4>
          <div className="flex flex-wrap gap-2">
            {preview.map((day) => (
              <div
                key={day.displayDay}
                className={`flex flex-col items-center justify-center w-12 h-12 rounded border text-xs font-medium ${
                  !day.isWorking
                    ? 'bg-gray-100 border-gray-300 text-gray-600 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400'
                    : day.shiftType === 'night'
                      ? 'bg-indigo-100 border-indigo-300 text-indigo-800 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-200'
                      : 'bg-amber-100 border-amber-300 text-amber-800 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-200'
                }`}
              >
                <span className="text-[10px] text-muted-foreground">Д{day.displayDay}</span>
                <span>
                  {!day.isWorking ? 'Вых' : day.shiftType === 'night' ? 'Ноч' : 'День'}
                </span>
              </div>
            ))}
            <div className="flex items-center text-muted-foreground text-xs">...</div>
          </div>
          <p className="text-xs text-muted-foreground">
            Цикл: {pattern.cycle_length} дней (повторяется)
          </p>
        </div>
      )}
    </div>
  );
};

